import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { InputText } from "primereact/inputtext";
import { Controller, useForm } from "react-hook-form";
import { classNames } from "primereact/utils";
import React, { useCallback, useContext, useEffect, useState } from "react";

import { ToastContext } from ".";
import "./styles.scss";
import { Button } from "primereact/button";
import { updateBuyerInfo } from "../../service/InvoiceService";

export const EditBuyerInfo = ({
  onReload,
  invInfo,
  display,
  setDisplay,
  token,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    clearErrors,
    setValue,
  } = useForm();
  const [loadingTaxCodeInfo, setLoadingTaxCodeInfo] = useState(false);
  const [loadingUpdateBuyerInfo, setLoadingUpdateBuyerInfo] = useState(0);

  const toast = useContext(ToastContext);

  useEffect(() => {
    if (invInfo) {
      reset({
        toName: invInfo.toName || "",
        toEmailAddress: invInfo.toEmailAddress || "",
        toTelecomNumber: invInfo.toTelecomNumber || "",
        toIdentification: invInfo.toIdentification || "",
        toAddress: invInfo.toAddress || "",
        toPartyName: invInfo.toPartyName || "",
        toPartyTaxId: invInfo.toPartyTaxId || "",
      });
    }
  }, [invInfo]);

  const onSubmit = useCallback(
    (data) => {
      if (!checkFormValid(data)) {
        return;
      }

      setLoadingUpdateBuyerInfo(Date.now);
      updateBuyerInfo({
        invoiceId: invInfo.invoiceId,
        buyerInfo: data,
        token: token,
      })
        .then((res) => {
          if (toast) {
            toast.current.show({
              severity: "success",
              summary: "Thông báo",
              detail: "Cập nhật thông tin người mua thành công",
              life: 3000,
            });
          }
          setDisplay(false);
          onReload();
        })
        .catch((err) => {
          if (toast) {
            toast.current.show({
              severity: "error",
              summary: "Thông báo",
              detail: err.message,
              life: 3000,
            });
          }
        })
        .finally(() => {
          setLoadingUpdateBuyerInfo(0);
        });
    },
    [invInfo, token, toast, onReload],
  );

  const getFormErrorMessage = (fieldName) => {
    return (
      errors[fieldName] && (
        <div className="mt-1 ml-1">
          <small className="p-error">{errors[fieldName].message}</small>
        </div>
      )
    );
  };

  const checkFormValid = useCallback((data) => {
    if (!data.toPartyName && !data.toName) {
      setError("root.form", {
        type: "manual",
        message: "Tên đơn vị hoặc tên người mua không được để trống",
      });
      setError(
        "toName",
        { message: "" },
        {
          shouldFocus: true,
        },
      );
      return false;
    }

    if (data.toPartyTaxId) {
      if (!data.toAddress) {
        setError("root.form", {
          type: "manual",
          message: "Địa chỉ không được để trống khi có mã số thuế",
        });
        setError(
          "toAddress",
          { message: "" },
          {
            shouldFocus: true,
          },
        );
        return false;
      }
    }

    clearErrors("root.form");
    return true;
  }, []);

  const getTaxCodeInfo = useCallback(
    (event, taxCode) => {
      event.preventDefault();

      if (!/^(?:[0-9]{12}|[0-9]{10}(?:-[0-9]{3})?)$/.test(taxCode)) {
        setError("toPartyTaxId", { message: "Mã số thuế không hợp lệ" });
        return;
      } else {
        clearErrors("toPartyTaxId");
      }

      setLoadingTaxCodeInfo(true);
      getTaxCodeInfo(taxCode, token)
        .then((res) => {
          const data = res.data;

          setValue("toPartyName", data.partyName);
          setValue("toAddress", data.fullAddress);
        })
        .catch((err) => {
          toast.current.show({
            severity: "error",
            summary: "Thông báo",
            detail: err.message,
            life: 3000,
          });
        })
        .finally(() => {
          setLoadingTaxCodeInfo(false);
        });
    },
    [token, toast],
  );

  return (
    <Dialog
      className="edit-buyer-info"
      header={
        <div className="edit-buyer-info-header flex justify-content-between align-items-center">
          <span>Chỉnh sửa thông tin người mua</span>
        </div>
      }
      footer={
        <div>
          <Button
            label="Đóng"
            icon="pi pi-times"
            onClick={() => setDisplay(false)}
            className="p-button-secondary p-button-sm"
          />
          <Button
            label="Lưu"
            loading={loadingUpdateBuyerInfo}
            icon="pi pi-check"
            onClick={handleSubmit(onSubmit)}
            className="p-button-info p-button-sm"
          />
        </div>
      }
      resizable={false}
      visible={display}
      onHide={() => setDisplay(false)}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
        {errors.root?.form && (
          <Message
            className="mb-3"
            severity="error"
            text={errors.root.form.message}
          />
        )}

        <div className="field">
          <label
            htmlFor="toPartyName"
            className={classNames({ "p-error": errors.toPartyName })}
          >
            Tên đơn vị
          </label>
          <Controller
            name="toPartyName"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                autoFocus
                className={classNames({
                  "p-invalid": fieldState.invalid,
                  "p-inputtext-sm": true,
                })}
              />
            )}
          />
          {getFormErrorMessage("toPartyName")}
        </div>
        <div className="field">
          <label
            htmlFor="toName"
            className={classNames({ "p-error": errors.toName })}
          >
            Tên người mua
          </label>
          <Controller
            name="toName"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                autoFocus
                className={classNames({
                  "p-invalid": fieldState.invalid,
                  "p-inputtext-sm": true,
                })}
              />
            )}
          />
          {getFormErrorMessage("toName")}
        </div>
        <div className="field">
          <label
            htmlFor="toAddress"
            className={classNames({ "p-error": errors.toAddress })}
          >
            Địa chỉ
          </label>
          <Controller
            name="toAddress"
            control={control}
            rules={{ maxLength: 400, minLength: 1 }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                autoFocus
                className={classNames({
                  "p-invalid": fieldState.invalid,
                  "p-inputtext-sm": true,
                })}
              />
            )}
          />
          {getFormErrorMessage("toAddress")}
        </div>
        <div className="field">
          <label
            htmlFor="toPartyTaxId"
            className={classNames({ "p-error": errors.toPartyTaxId })}
          >
            Mã số thuế
          </label>
          <Controller
            name="toPartyTaxId"
            rules={{
              pattern: {
                value: /^[0-9]{12}$|^[0-9]{10}(-[0-9]{3})?$/,
                message: "Mã số thuế không hợp lệ",
              },
            }}
            control={control}
            render={({ field, fieldState }) => (
              <div className="p-inputgroup">
                <InputText
                  id={field.name}
                  {...field}
                  autoFocus
                  className={classNames({
                    "p-invalid": fieldState.invalid,
                    "p-inputtext-sm": true,
                  })}
                />
                <Button
                  loading={loadingTaxCodeInfo}
                  onClick={(e) => getTaxCodeInfo(e, field.value)}
                  disabled={!field.value}
                  label="Lấy thông tin"
                  icon="pi pi-search"
                  className={classNames({ "p-button-info p-button-sm": true })}
                />
              </div>
            )}
          />
          {getFormErrorMessage("toPartyTaxId")}
        </div>
        <div className="field">
          <label
            htmlFor="toIdentification"
            className={classNames({ "p-error": errors.toIdentification })}
          >
            Căn cước công dân
          </label>
          <Controller
            name="toIdentification"
            control={control}
            rules={{
              maxLength: {
                value: 12,
                message: "Căn cước công dân phải có 12 chữ số",
              },
              minLength: {
                value: 12,
                message: "Căn cước công dân phải có 12 chữ số",
              },
            }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                autoFocus
                className={classNames({
                  "p-invalid": fieldState.invalid,
                  "p-inputtext-sm": true,
                })}
              />
            )}
          />
          {getFormErrorMessage("toIdentification")}
        </div>
        <div className="field">
          <label
            htmlFor="toEmailAddress"
            className={classNames({ "p-error": errors.toEmailAddress })}
          >
            Email
          </label>
          <Controller
            name="toEmailAddress"
            control={control}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                autoFocus
                className={classNames({
                  "p-invalid": fieldState.invalid,
                  "p-inputtext-sm": true,
                })}
              />
            )}
          />
          {getFormErrorMessage("toEmailAddress")}
        </div>
        <div className="field">
          <label
            htmlFor="toTelecomNumber"
            className={classNames({ "p-error": errors.toTelecomNumber })}
          >
            Số điện thoại
          </label>
          <Controller
            name="toTelecomNumber"
            control={control}
            rules={{
              maxLength: {
                value: 20,
                message: "Số điện thoại phải có tối đa 20 chữ số",
              },
              pattern: {
                value: /^[0-9]+$/,
                message: "Số điện thoại phải là số",
              },
            }}
            render={({ field, fieldState }) => (
              <InputText
                id={field.name}
                {...field}
                autoFocus
                className={classNames({
                  "p-invalid": fieldState.invalid,
                  "p-inputtext-sm": true,
                })}
              />
            )}
          />
          {getFormErrorMessage("toTelecomNumber")}
        </div>
      </form>
    </Dialog>
  );
};
